/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { OperationRetryStrategies } from 'src/core/utils/api/operationRetryStrategies';
import { getEventBus } from 'src/core/utils/eventBus';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { modelTrainingStatusAnalyzer } from 'src/core/utils/trainingHelpers';
import { getTrainingService } from 'src/luis/core/services/training/trainingService';
import { TrainingGetStatusSwagger } from 'src/luis/core/services/training/trainingSwagger';
import { TrainingStore } from 'src/luis/core/stores/TrainingStore';
import { modelTrainingStatusSwaggerToModelTrainingStatus } from 'src/luis/modules/training/trainingMappers';
import { ModelTrainingStatus, TrainingModes, TrainingState } from 'src/luis/types/trainingTypes';

export type TrainingHandlerGroup = ReturnType<typeof createTrainingHandlerGroup>;

const eventBus = getEventBus();

/**
 * Creates a handler group of functions for the training to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createTrainingHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    /**
     * Issues a training request for the current
     * application and handles its status.
     */
    const train = trainAppVersion(rootStore);

    return { train };
};

/**
 * Issues a training request for the current
 * application and handles its status.
 */
export const trainAppVersion = (rootStore: RootStore) => async (params?: HandlerParams<{ mode?: TrainingModes }>) => {
    const { context, mode = 'Standard' } = params;
    const { appId, versionId } = context;
    const logger = getLogger();

    const trainingStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId)?.trainingStore;

    const shouldTrainingReflect = (overallStatus: TrainingState) => overallStatus !== 'fail' && overallStatus !== 'upToDate';

    const logTimedActivity = logger.createTimedActivityLogger<boolean>('Train');

    if (!context.forceRefresh && trainingStore?.serviceStatus !== 'ready') {
        return;
    }

    trainingStore.setLastTrainingMode(mode);
    trainingStore.setCurrentTrainingStatus({ overallStatus: 'queued' });

    let wasUpToDate = false;
    try {
        wasUpToDate = await logTimedActivity(
            getTrainingService({ context }).train({
                mode,
                trainingStatusHandler: createTrainingOperationHandler(<string>appId, mode, trainingStore)
            })
        );
    } catch (e) {
        trainingStore.setCurrentTrainingStatus({ overallStatus: 'fail', trainingFailureReason: (<Error>e).message });
    }

    if (wasUpToDate) {
        trainingStore.setCurrentTrainingStatus({ overallStatus: 'upToDate' });
    }

    trainingStore.setLastTrainingStatus(trainingStore.currentTrainingStatus);

    if (shouldTrainingReflect(trainingStore.currentTrainingStatus.overallStatus)) {
        trainingStore.setLastTrainingTime(new Date());
    }
};

/**
 * Creates a long running function handler to poll the training
 * service to check for the training status.
 *
 * @todo Better separation for conversation and documents apps.
 *
 * @returns A handler to use with the service.
 */
const createTrainingOperationHandler = (
    appId: string,
    mode: TrainingModes,
    trainingStore: TrainingStore
): OperationHandler<TrainingGetStatusSwagger> => {
    const handleCall = (swaggerData: TrainingGetStatusSwagger) => {
        const modelsData = modelTrainingStatusSwaggerToModelTrainingStatus(swaggerData);

        const failedModel: ModelTrainingStatus = modelTrainingStatusAnalyzer.getFailedModel(modelsData);

        if (modelTrainingStatusAnalyzer.isQueued(modelsData)) {
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'queued' });
        } else if (failedModel !== undefined) {
            trainingStore.setIsTrained(false);
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'fail', failedModel });
        } else if (modelTrainingStatusAnalyzer.isUpToDate(modelsData)) {
            trainingStore.setIsTrained(true);
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'upToDate' });
        } else if (modelTrainingStatusAnalyzer.isCompleted(modelsData)) {
            trainingStore.setIsTrained(true);
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'success', totalModelsCount: modelsData.length });
            eventBus.emit('notifyTrainingSucceeded', { appId, trainedModels: modelsData });
        } else if (modelTrainingStatusAnalyzer.isCollectingData(modelsData)) {
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'collectingData', totalModelsCount: modelsData.length });
        } else if (modelTrainingStatusAnalyzer.isUploadingModel(modelsData)) {
            trainingStore.setCurrentTrainingStatus({ overallStatus: 'uploadingModel', totalModelsCount: modelsData.length });
        } else {
            trainingStore.setIsTrained(true);
            trainingStore.setCurrentTrainingStatus({
                overallStatus: 'inProgress',
                totalModelsCount: modelsData.length,
                trainedModelsCount: modelTrainingStatusAnalyzer.getCompletedModels(modelsData)
            });
        }
    };

    const getDelayStrategy = () => {
        if (mode === 'Standard') {
            return OperationDelayStrategies.exponentialDelayStrategy;
        }

        return () => LuisConstants.Luis.NeuralModelTrainingDelayTime;
    };

    return {
        handleCall,
        timeToDelay: getDelayStrategy(),
        retryStrategy: OperationRetryStrategies.trainingRetryStrategy
    };
};
